import React from 'react';
import { graphql, useFragment } from 'react-relay';
import { MultiSelectInlineEditViewWithIsEditable } from '@atlassian/jira-issue-field-multi-select-inline-edit-full/src/ui/multi-select/index.tsx';
import type { multiSelect_nativeIssueTable_MultiSelectCell$key } from '@atlassian/jira-relay/src/__generated__/multiSelect_nativeIssueTable_MultiSelectCell.graphql';
import { INLINE_EDIT_ANALYTICS_ATTRIBUTES } from '../common/inline-edit-analytics-attributes.tsx';
import { useLazyLoadEditability } from '../../../../services/lazy-editability-loader/index.tsx';
import {
	useIsInlineEditingExtendedFieldSupportEnabled,
	useIsSingleLineRowHeightEnabled,
} from '../../../../controllers/features/selectors.tsx';

export type Props = {
	fieldRef: multiSelect_nativeIssueTable_MultiSelectCell$key;
};

export const MultiSelectCell = ({ fieldRef }: Props) => {
	const data = useFragment<multiSelect_nativeIssueTable_MultiSelectCell$key>(
		graphql`
			fragment multiSelect_nativeIssueTable_MultiSelectCell on JiraMultipleSelectField
			@argumentDefinitions(isInlineEditingEnabled: { type: "Boolean!" }) {
				id
				lazyIsEditableInIssueView @include(if: $isInlineEditingEnabled)
				...multiSelect_issueFieldMultiSelectInlineEditFull_MultiSelectInlineEditViewWithIsEditable_fragmentRef
			}
		`,
		fieldRef,
	);

	const isInlineEditingExtendedFieldSupportEnabled =
		useIsInlineEditingExtendedFieldSupportEnabled();
	const isSingleLineRowHeightEnabled = useIsSingleLineRowHeightEnabled();
	// If the property does not exist in fragment data then we know it has been conditionally excluded because inline
	// editing is disabled. In this case we explicitly set isEditable to false to bypass the lazy editability query.
	const isEditable =
		'lazyIsEditableInIssueView' in data && isInlineEditingExtendedFieldSupportEnabled
			? data.lazyIsEditableInIssueView
			: false;
	useLazyLoadEditability(data.id, isEditable);

	return (
		<MultiSelectInlineEditViewWithIsEditable
			attributes={INLINE_EDIT_ANALYTICS_ATTRIBUTES}
			spacing="default"
			editViewPopup
			editViewPopupAlignBlock="center"
			fragmentRef={data}
			isEditable={isEditable ?? false}
			menuPosition="fixed"
			readViewFitContainerHeight
			isTruncated
			isTruncatedWithHoverPopover={isSingleLineRowHeightEnabled}
		/>
	);
};
